import React, { useEffect, useState } from "react";
import { Grid, TextField, MenuItem } from "@mui/material";
import api from "../../globals/axios";
import * as yup from "yup";

const addressValidationSchema = yup.object().shape({
  pincode: yup
    .string()
    .required("Pincode is required")
    .test("valid-pincode", "Pincode is not valid", async (pincode) => {
      if (pincode && pincode.length === 6) {
        const response = await api.get(`common/pincode/${pincode}`);
        const postOfficesData = response.data[0]?.PostOffice || [];
        return postOfficesData.length > 0;
      } else {
        return false;
      }
    }),
});

const AddressForm = ({
  showAllFields,
  t,
  handleFieldChange,
  profileData,
  updateProfileData,
  fieldKeyMap = {},
  errors = {},
}) => {
  const [postOffices, setPostOffices] = useState(
    profileData[fieldKeyMap.post_office]
      ? [{ Name: profileData[fieldKeyMap.post_office] }]
      : []
  );
  const [state, setState] = useState(profileData[fieldKeyMap.state] || "");
  const [district, setDistrict] = useState(
    profileData[fieldKeyMap.district] || ""
  );
  const [pincode, setPincode] = useState(
    profileData[fieldKeyMap.pin_code] || ""
  );
  const [pincodeError, setPincodeError] = useState("");
  const [address, setAddress] = useState(
    profileData[fieldKeyMap.address] || ""
  );

  useEffect(() => {
    addressValidationSchema
      .validate({ pincode })
      .then(() => {
        setPincodeError("");
      })
      .catch((error) => {
        setPincodeError(error.message);
      });

    if (pincode && pincode.length === 6) {
      api
        .get(`common/pincode/${pincode}`)
        .then((response) => {
          const postOfficesData = response.data[0]?.PostOffice;
          if (postOfficesData && postOfficesData.length > 0) {
            const firstPostOffice = postOfficesData[0];
            if (firstPostOffice) {
              const { State, District, Name } = firstPostOffice;
              setState(State);
              setDistrict(District);
              setPostOffices(postOfficesData);
              // Update profileData with new values
              updateProfileData({
                [fieldKeyMap.state]: State,
                [fieldKeyMap.district]: District,
                [fieldKeyMap.post_office]: Name,
              });
            }
          } else {
            setPostOffices([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching post offices:", error);
        });
    } else {
      setState("");
      setDistrict("");
      setPostOffices([]);
      // Update profileData with empty values
      updateProfileData({
        [fieldKeyMap.state]: "",
        [fieldKeyMap.district]: "",
        [fieldKeyMap.post_office]: "",
      });
    }
  }, [pincode]);

  useEffect(() => {
    setPincode(profileData[fieldKeyMap.pin_code] || "");
    setState(profileData[fieldKeyMap.state] || "");
    setDistrict(profileData[fieldKeyMap.district] || "");
    setAddress(profileData[fieldKeyMap.address] || "");
    setPostOffices(
      profileData[fieldKeyMap.post_office]
        ? [{ Name: profileData[fieldKeyMap.post_office] }]
        : []
    );
  }, [profileData, fieldKeyMap]);

  return (
    <Grid container spacing={2}>
      {showAllFields && (
        <>
          <Grid item xs={12}>
            <TextField
              label={t("Pincode")}
              type="number"
              fullWidth
              value={pincode}
              onChange={(e) => {
                setPincode(e.target.value);
                handleFieldChange(fieldKeyMap.pin_code, e.target.value);
              }}
              error={Boolean(pincodeError)}
              helperText={pincodeError}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField label={t("State")} disabled fullWidth value={state} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("District")}
              disabled
              fullWidth
              value={district}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("Post Office")}
              select
              fullWidth
              value={profileData[fieldKeyMap.post_office] || ""}
              onChange={(e) =>
                updateProfileData({ [fieldKeyMap.post_office]: e.target.value })
              }
            >
              {postOffices.map((office) => (
                <MenuItem key={office.Name} value={office.Name}>
                  {office.Name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <TextField
          label={t("Address")}
          multiline
          fullWidth
          rows={4}
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            handleFieldChange(fieldKeyMap.address, e.target.value); // Ensure this updates profileData
          }}
          error={Boolean(errors.address_line1)}
          helperText={errors.address_line1}
        />
      </Grid>
    </Grid>
  );
};

export default AddressForm;
